<template>
    <vx-box
        color="white"
        :disabled="!hasValue && !props.loading"
        class="flex flex-col p-6 relative"
        :style="{
            minHeight: props.minHeight,
        }"
    >
        <div class="flex justify-between items-baseline">
            <div
                v-if="$slots.title || $slots.subtitle"
                class="flex md:flex-row flex-col gap-4"
                :class="{
                    'justify-end': !$slots.title && !$slots.subtitle,
                    'justify-between': $slots.title || $slots.subtitle,
                }"
            >
                <div v-if="$slots.title || $slots.subtitle" class="pb-1.5">
                    <div
                        class="flex justify-between items-center"
                        :class="hasValue || props.loading ? 'text-slate-800' : 'text-slate-500'"
                    >
                        <span
                            class="text-sm font-normal"
                            :title="$slots.title() && getSlotChildrenText($slots.title())"
                        >
                            <slot name="title"></slot>
                        </span>
                    </div>
                    <div v-if="$slots.subtitle" class="text-xs leading-none text-slate-400">
                        <slot name="subtitle"></slot>
                    </div>
                </div>
            </div>

            <template v-if="props.href">
                <vx-button :href="props.href" :disabled="loading" size="xs" color="muted-light">
                    Details <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
                </vx-button>
            </template>
        </div>

        <div
            class="space-y-2 transition delay-150 duration-300 will-change-transform z-10"
            :class="{
                'pointer-events-none opacity-0 motion-safe:translate-y-4': props.loading || props.error,
            }"
        >
            <div v-if="!hasValue && !props.loading && !props.error" class="">
                <span class="text-5xl font-light">-</span>
            </div>
            <vx-popover
                v-else
                arrow
                hover
                placement="top"
                @click="copyValue()"
                class="min-h-[36px] cursor-pointer text-5xl font-bold text-slate-600"
                data-test="number"
            >
                <slot></slot>
                <template v-slot:content>
                    <div class="text-center" @click="copyValue()">
                        <template v-if="props.percentage">
                            {{ formatFractionToPercent(props.value, true) }}
                        </template>
                        <template v-else>
                            {{ formatNumber(props.value) }}
                        </template>
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
            <div v-if="hasDifference" data-test="difference">
                <template v-if="differenceFormatted === '0.00'">
                    <div class="inline-block w-5 h-0.5 mr-2 bg-slate-400"></div>
                    <span class="text-slate-400 font-light text-xs">No change</span>
                </template>
                <template v-else>
                    <vx-badge size="xs" :color="props.color === 'good' ? 'primary' : 'slate'">
                        {{ props.direction === 'up' ? '+' : '-' }}{{ differenceFormatted }}
                        <font-awesome-icon
                            widthAuto
                            :class="{
                                'text-sky-500': props.color === 'good',
                                'text-slate-500': props.color === 'bad',
                            }"
                            :icon="faPercent"
                        ></font-awesome-icon>
                    </vx-badge>
                </template>
            </div>
        </div>
        <transition
            enterFromClass="opacity-0 -translate-y-4"
            enterToClass="opacity-100 translate-y-0"
            leaveFromClass="opacity-100 translate-y-0"
            leaveToClass="opacity-0 -translate-y-4"
        >
            <div
                v-if="
                    !(props.loading || props.error) &&
                    hasDifference &&
                    props.direction &&
                    differenceFormatted !== '0.00'
                "
                class="absolute transition inset-e-8 inset-be-0 text-7xl h-16 overflow-hidden"
            >
                <template v-if="props.direction === 'up'">
                    <font-awesome-icon
                        widthAuto
                        :class="{
                            'text-sky-100': props.color === 'good',
                            'text-slate-100': props.color === 'bad',
                        }"
                        class="translate-y-2"
                        :icon="faArrowUpLong"
                    ></font-awesome-icon>
                </template>
                <template v-else>
                    <font-awesome-icon
                        widthAuto
                        :class="{
                            'text-sky-100': props.color === 'good',
                            'text-slate-100': props.color === 'bad',
                        }"
                        :icon="faArrowDownLong"
                    ></font-awesome-icon>
                </template>
            </div>
            <div
                v-else-if="!(props.loading || props.error) && !hasDifference && hasValue && props.backgroundIcon"
                class="absolute transition inset-e-8 inset-be-0 text-7xl h-16 overflow-hidden"
            >
                <font-awesome-icon widthAuto class="text-sky-100" :icon="props.backgroundIcon" />
            </div>
        </transition>

        <div
            v-if="$slots.explainHeader"
            class="absolute inset-e-2 inset-be-2 transition delay-150 duration-300"
            :class="{
                'pointer-events-none opacity-0': !hasValue || props.loading || props.error,
            }"
        >
            <vx-button
                @click="explainModal = true"
                data-test="explain-btn"
                :aria-label="
                    $slots.title ? `Explain ${getSlotChildrenText($slots.title()).trim()}` : 'Explain this metric'
                "
                size="xs"
                color="transparent"
                type="button"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faCircleQuestion"></font-awesome-icon>
            </vx-button>
        </div>

        <card-loading-bars :loading="props.loading" :disabled="props.error" />

        <vx-modal size="sm" v-if="$slots.explainHeader" v-model:visible="explainModal" :padding="false">
            <template v-slot:header>
                <div class="p-6 bg-white border-t-0 border-x-0 border-b border-solid border-b-slate-100">
                    <slot name="explainHeader"></slot>
                </div>
            </template>

            <div class="p-6 bg-slate-50 [&_p_strong]:text-slate-700 [&_p]:mb-6 lg:py-10 lg:px-12">
                <slot name="explainContent"></slot>
            </div>
        </vx-modal>
    </vx-box>
</template>
<script setup>
import { computed, ref } from 'vue';
import { VxBox, VxModal, VxButton, VxBadge, VxPopover } from '@voxie/frontend-components';
import { formatPercentage } from '~/components/filters';
import { getSlotChildrenText, formatFractionToPercent, formatNumber } from '../../../utils/helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { faArrowDownLong, faArrowUpLong, faPercent, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import CardLoadingBars from './CardLoadingBars.vue';

const props = defineProps({
    percentage: {
        type: Boolean,
        default: false,
    },
    difference: {
        type: Number,
    },
    color: {
        type: String,
    },
    direction: {
        type: String,
    },
    value: {},
    loading: {
        type: Boolean,
        required: false,
        default: false,
    },
    error: {
        type: Boolean,
        default: false,
    },
    minHeight: {
        type: String,
        required: false,
        default: '160px',
    },
    href: {
        type: String,
    },
    backgroundIcon: {
        type: Object,
    },
});

const explainModal = ref(false);

const hasDifference = computed(() => props.difference !== null && props.difference !== undefined);
const hasValue = computed(() => props.value !== null && props.value !== undefined && !props.error);

const differenceFormatted = computed(() => {
    return formatPercentage(Math.abs(props.difference), false);
});

const copyValue = () => {
    window.navigator.clipboard.writeText(
        props.percentage
            ? `${formatNumber(props.value * 100, { maximumFractionDigits: 4, useGrouping: false })}%`
            : props.value
    );
};
</script>
